import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';

import { ApplicationFormStore } from './services/application-form.store';
import { PortalAvailabilityService } from '../../core/cycle/portal-availability';
import { ToastService } from '../../core/toast/toast.service';

/**
 * Keep an applicant out of the form when they cannot save what they write in it.
 *
 * Without this, the form was reachable by button or by pasted URL at any time after the
 * portal opened, forever. The server always refused the save — nothing was ever stored
 * wrongly — but the refusal came at the END, after the applicant had filled in as much
 * as they cared to. The wasted effort was the whole defect.
 *
 * ## It asks the server, it does not compute a deadline
 *
 * The obvious implementation — "is now past `submissionDeadline`" — is wrong, and would
 * have broken a feature shipped the same week. A requested HP→SLIP-Law-Clerk transfer
 * re-opens editing until `programChangeDeadline`, which is AFTER the submission
 * deadline. So "may this person edit" depends on their own application's state, not on
 * the calendar, and only the server can answer it. `OasisPublicConfig` states the rule
 * outright: its dates are for display and progressive disclosure, never authorization.
 *
 * So the structure mirrors `evaluateSaveAuthorization`, which is the single authority:
 *
 *   - **An application exists** → obey the server's `flags.canEdit`. This gets the
 *     transfer window, the legacy resubmission window, and anything added later for
 *     free, because it re-derives none of them.
 *   - **No application yet** → the submission window is the whole answer. With a null
 *     header the server consults only the primary window too: there is nothing to
 *     transfer and nothing to resubmit. Client and server agree by construction, and
 *     this is the ONLY deadline question the client may answer for itself.
 *
 * ## It fails OPEN
 *
 * A load failure lets the applicant through, matching `sectionAccessGuard` and the
 * config service's stated policy — failing closed would shut a live cycle over one
 * transient 500. The cost is bounded because the server is the real gate: they reach a
 * form whose first save is refused, with an accurate message.
 *
 * Ordered BEFORE `sectionAccessGuard` in the route's `canActivateChild`. Both hydrate
 * the store, and `loadFromServer` is idempotent after its first success, so the second
 * call is free.
 */
export const submissionWindowGuard: CanActivateChildFn = async () => {
	const store = inject(ApplicationFormStore);
	const availability = inject(PortalAvailabilityService);
	const router = inject(Router);
	const toast = inject(ToastService);

	let loaded = true;
	try {
		await store.loadFromServer();
	} catch {
		loaded = false;
	}

	// Tracked explicitly rather than inferred from empty state. On a failed load the
	// store looks exactly like "no application", which would send a transferring
	// applicant down the window-closed branch and lock them out of the one window that
	// is open for them.
	if (!loaded) return true;

	const canEdit = store.serverCanEdit();

	// An explicit NO from the server. The only branch that refuses an existing
	// application, and it refuses regardless of the calendar — the server knows things
	// the cycle dates do not, such as a withdrawn or ineligible application.
	if (canEdit === false) {
		toast.info('Your application can no longer be edited. The deadline for this cycle has passed.');
		return router.createUrlTree(['/dashboard']);
	}

	// An explicit YES. Covers the transfer case, where the submission deadline has
	// passed and the applicant must still be let in.
	if (canEdit === true) return true;

	/*
	 * UNKNOWN — nobody has asked the server about this applicant yet, which happens when
	 * the load found no application. Treated as "no application", because that is what it
	 * means: `loadFromServer` obtains flags whenever it finds one.
	 *
	 * The submission window is the right authority here and agrees with the server by
	 * construction — for a null header `evaluateSaveAuthorization` also consults only the
	 * primary window, since there is nothing to transfer and nothing to resubmit.
	 *
	 * This must NOT be reached by an applicant who has an application. It was, before
	 * `serverCanEdit` became a tri-state: a successful first save left the answer absent,
	 * the old boolean reported `false`, and the branch above ejected them.
	 */
	if (!availability.isSubmissionClosed()) return true;

	const deadline = availability.deadlineLabel();
	toast.info(
		deadline
			? `Applications closed on ${deadline}. A new application can no longer be started.`
			: 'Applications for this cycle are closed. A new application can no longer be started.',
	);
	return router.createUrlTree(['/dashboard']);
};
